/**
 * Lightweight client-side cache for API GET responses.
 * Uses sessionStorage so it survives in-app navigation but not new sessions.
 * Stale-while-revalidate pattern: return cached value INSTANTLY, refetch in background.
 */
import axios from 'axios';

const NS = 'jsai_cache_v1:';

const memCache = new Map();

function readPersisted(key) {
  try {
    const raw = sessionStorage.getItem(NS + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writePersisted(key, value) {
  try {
    sessionStorage.setItem(NS + key, JSON.stringify(value));
  } catch {}
}

/**
 * cachedGet — returns cached value (if fresh) or null while fetching in background.
 *   url       — full URL to fetch
 *   options   — axios config (headers etc.)
 *   ttlMs     — cache time-to-live (default 60s)
 *   onUpdate  — optional callback fired with fresh data once the background fetch resolves
 */
export async function cachedGet(url, options = {}, ttlMs = 60000, onUpdate = null) {
  const key = url + JSON.stringify(options.params || {});
  const now = Date.now();

  // Try in-memory first
  let entry = memCache.get(key);
  if (!entry) {
    entry = readPersisted(key);
    if (entry) memCache.set(key, entry);
  }

  const fresh = entry && (now - entry.ts) < ttlMs;

  if (fresh) {
    // Background revalidation if more than 50% TTL elapsed
    if (now - entry.ts > ttlMs / 2 && !entry._revalidating) {
      entry._revalidating = true;
      axios.get(url, options).then((res) => {
        const updated = { ts: Date.now(), data: res.data };
        memCache.set(key, updated);
        writePersisted(key, updated);
        onUpdate?.(res.data);
      }).catch(() => {}).finally(() => { if (memCache.get(key)) memCache.get(key)._revalidating = false; });
    }
    return { data: entry.data, fromCache: true };
  }

  // No fresh cache — fetch and cache
  const res = await axios.get(url, options);
  const updated = { ts: Date.now(), data: res.data };
  memCache.set(key, updated);
  writePersisted(key, updated);
  return { data: res.data, fromCache: false };
}

export function invalidateCache(prefix = '') {
  for (const k of Array.from(memCache.keys())) {
    if (!prefix || k.includes(prefix)) memCache.delete(k);
  }
  try {
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith(NS) && (!prefix || k.includes(prefix))) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {}
}
